import React, { useState, useContext } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaEnvelope, FaPaperPlane, FaTimes, FaUser, FaBuilding, FaFileAlt, FaDollarSign, FaCalendarAlt, FaLink } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

/**
 * EmailPreviewModal - Shows email preview and allows customization before sending
 * 
 * Works for:
 * - Proposals (type: 'proposal')
 * - Change Orders (type: 'change_order')
 * - Invoices (type: 'invoice')
 */
const EmailPreviewModal = ({ 
  show, 
  onHide, 
  type,           // 'proposal', 'change_order', or 'invoice'
  documentId,     // projectId for proposals, changeOrderId for change orders, invoiceId for invoices
  emailData,      // Data returned from document generation
  onEmailSent,    // Callback after email is sent (optional)
  signingUrl      // The signing URL (for proposals and change orders)
}) => {
  const { token } = useContext(AuthContext);
  const [customMessage, setCustomMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Determine the API endpoint based on type
  const getApiEndpoint = () => {
    switch (type) {
      case 'proposal':
        return `/api/email/proposal/${documentId}/send`;
      case 'change_order':
        return `/api/email/change-order/${documentId}/send`;
      case 'invoice':
        return `/api/email/invoice/${documentId}/send`;
      default:
        return null;
    }
  };

  // Get document-specific labels and styling
  const getTypeConfig = () => {
    switch (type) {
      case 'proposal':
        return {
          title: 'Send Proposal Email',
          color: '#007bff',
          icon: <FaFileAlt />,
          subjectPrefix: 'Your Proposal is Ready',
          buttonText: 'Send Proposal Email'
        };
      case 'change_order':
        return {
          title: 'Send Change Order Email',
          color: '#fd7e14',
          icon: <FaFileAlt />,
          subjectPrefix: `Change Order ${emailData?.changeOrderNumber || ''}`,
          buttonText: 'Send Change Order Email'
        };
      case 'invoice':
        return {
          title: 'Send Invoice Email',
          color: '#28a745',
          icon: <FaFileAlt />,
          subjectPrefix: `Invoice ${emailData?.invoiceNumber || ''}`,
          buttonText: 'Send Invoice Email'
        };
      default:
        return {
          title: 'Send Email',
          color: '#6c757d',
          icon: <FaEnvelope />,
          subjectPrefix: 'Document',
          buttonText: 'Send Email'
        };
    }
  };

  const config = getTypeConfig();

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle send email
  const handleSendEmail = async () => {
    const endpoint = getApiEndpoint();
    if (!endpoint) {
      toast.error('Invalid document type');
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          customMessage: customMessage.trim() || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send email');
      }

      toast.success(`Email sent successfully to ${data.sentTo || emailData?.clientEmail}`);
      
      if (onEmailSent) {
        onEmailSent(data);
      }
      
      onHide();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  // Handle skip (close without sending)
  const handleSkip = () => {
    // Show the signing link in a toast so user can still copy it
    if (signingUrl) {
      const LinkMessage = () => (
        <div>
          <p className="mb-2"><strong>Document created successfully!</strong></p>
          <p className="mb-2">Email was not sent. You can copy the signing link:</p>
          <div className="mt-2">
            <input 
              type="text" 
              value={signingUrl} 
              readOnly 
              style={{ 
                width: '100%', 
                padding: '8px',
                marginBottom: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px'
              }}
              onClick={(e) => e.target.select()}
            />
            <button 
              style={{
                padding: '6px 12px',
                backgroundColor: '#0d6efd',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => {
                navigator.clipboard.writeText(signingUrl);
                toast.info('Link copied to clipboard!');
              }}
            >
              📋 Copy Link
            </button>
          </div>
        </div>
      );
      
      toast.info(<LinkMessage />, { autoClose: false });
    } else {
      toast.info('Document created. Email was not sent.');
    }
    
    onHide();
  };

  // Check if we can send
  const canSend = emailData?.clientEmail;

  return (
    <Modal show={show} onHide={handleSkip} size="lg" centered backdrop="static">
      <Modal.Header style={{ backgroundColor: config.color, color: 'white' }}>
        <Modal.Title>
          <FaEnvelope className="me-2" />
          {config.title}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {!canSend && (
          <Alert variant="warning" className="mb-4">
            <strong>No email address found!</strong> This client doesn't have an email address on file. 
            You can still copy the signing link and send it manually.
          </Alert>
        )}

        {/* Email Preview Section */}
        <div className="email-preview mb-4">
          <h6 className="text-muted mb-3">Email Preview</h6>
          
          <div className="border rounded p-3 bg-light">
            {/* To */}
            <Row className="mb-2">
              <Col sm={2}><strong>To:</strong></Col>
              <Col sm={10}>
                <FaUser className="me-2 text-muted" />
                {emailData?.clientName || 'Client'} 
                {emailData?.clientEmail && (
                  <span className="text-muted"> &lt;{emailData.clientEmail}&gt;</span>
                )}
                {!emailData?.clientEmail && (
                  <span className="text-danger"> (No email)</span>
                )}
              </Col>
            </Row>

            {/* From */}
            <Row className="mb-2">
              <Col sm={2}><strong>From:</strong></Col>
              <Col sm={10}>
                <FaBuilding className="me-2 text-muted" />
                Your Company Name &lt;notifications@cmsystem.cloud&gt;
              </Col>
            </Row>

            {/* Subject */}
            <Row className="mb-3">
              <Col sm={2}><strong>Subject:</strong></Col>
              <Col sm={10}>
                {config.subjectPrefix} - {emailData?.projectName || 'Project'}
              </Col>
            </Row>

            <hr />

            {/* Document Details */}
            <div className="document-details mb-3">
              <Row>
                <Col md={6}>
                  <p className="mb-1">
                    <FaFileAlt className="me-2" style={{ color: config.color }} />
                    <strong>Project:</strong> {emailData?.projectName || 'N/A'}
                  </p>
                  {type === 'change_order' && (
                    <p className="mb-1">
                      <strong>Change Order:</strong> {emailData?.changeOrderNumber}
                    </p>
                  )}
                  {type === 'invoice' && (
                    <p className="mb-1">
                      <strong>Invoice:</strong> {emailData?.invoiceNumber}
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  {(type === 'change_order' || type === 'invoice') && emailData?.amount && (
                    <p className="mb-1">
                      <FaDollarSign className="me-2 text-success" />
                      <strong>Amount:</strong> {formatCurrency(emailData.amount || emailData.totalDue)}
                    </p>
                  )}
                  {type === 'invoice' && emailData?.dueDate && (
                    <p className="mb-1">
                      <FaCalendarAlt className="me-2 text-info" />
                      <strong>Due Date:</strong> {formatDate(emailData.dueDate)}
                    </p>
                  )}
                </Col>
              </Row>
            </div>

            {/* Signing Link Preview */}
            {signingUrl && (
              <div className="signing-link-preview p-2 bg-white border rounded mb-3">
                <small className="text-muted d-block mb-1">
                  <FaLink className="me-1" /> 
                  {type === 'invoice' ? 'Invoice Link:' : 'Signing Link:'}
                </small>
                <code style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
                  {signingUrl}
                </code>
              </div>
            )}
          </div>
        </div>

        {/* Custom Message Section */}
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>Add a Personal Message</strong>
            <small className="text-muted ms-2">(Optional)</small>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Add a personal note to your client... (e.g., 'Thanks for meeting with me yesterday! Let me know if you have any questions.')"
            disabled={isSending}
          />
          <Form.Text className="text-muted">
            This message will appear in a highlighted box at the top of the email.
          </Form.Text>
        </Form.Group>

        {/* What the client will receive */}
        <Alert variant="info" className="mb-0">
          <small>
            <strong>What your client will receive:</strong>
            <ul className="mb-0 mt-1">
              <li>A professional email with your company branding</li>
              {customMessage && <li>Your personal message highlighted at the top</li>}
              {type !== 'invoice' && <li>A button to view and electronically sign the document</li>}
              {type === 'invoice' && <li>Invoice details and amount due</li>}
              <li>Your contact information in the footer</li>
            </ul>
          </small>
        </Alert>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button 
          variant="outline-secondary" 
          onClick={handleSkip}
          disabled={isSending}
        >
          <FaTimes className="me-1" />
          Skip Email
        </Button>
        
        <Button 
          variant="primary" 
          onClick={handleSendEmail}
          disabled={!canSend || isSending}
          style={{ backgroundColor: config.color, borderColor: config.color }}
        >
          {isSending ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Sending...
            </>
          ) : (
            <>
              <FaPaperPlane className="me-2" />
              {config.buttonText}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailPreviewModal;
